import { track } from "@amplitude/analytics-react-native";
import { memo, ReactNode, useCallback } from "react";
import { View } from "react-native";
import { ADDONS, GATHERING_QR_CODE } from "src/api/featureFlags";
import {
	FullPublicPhotoFragmentFragment,
	GatheringHeaderInfoFragmentFragment,
} from "src/api/graphql/__generated__/graphql";
import parseUrlForLabel from "src/components/Addon/helpers/parseUrlForLabel";
import GatheringHeader from "src/components/GatheringHeader";
import { SharedGatheringHeaderProps } from "src/components/GatheringHeader/types";
import { HEADER_SPACING } from "src/components/Header/constants";
import OpacityView from "src/components/OpacityView";
import PillButton from "src/components/PillButton";
import RoundedButton from "src/components/RoundedButton";
import Touchable from "src/components/Touchable";
import config from "src/config";
import { parseGatheringThumbnailUrl } from "src/constants/GatheringThumbnail";
import abbreviateBigNumber from "src/helpers/abbreviateBigNumber";
import parseGatheringPreviewTemplateUrl from "src/helpers/parseV0TemplateUrl/parseGatheringPreviewTemplateUrl";
import useDevice from "src/hooks/useDevice";
import useRouter from "src/hooks/useRouter";
import IconPhoto from "src/icons/IconPhoto";
import IconShare from "src/icons/IconShare";
import IconView from "src/icons/IconView";
import { useModal } from "src/modals";
import GlobalModal from "src/modals/configs/GlobalModal";
import useGatheringHomePhotoSpacing from "src/screens/(internal)/(GatheringHome)/GatheringHome/hooks/useGatheringHomePhotoSpacing";
import { paddingLarge, paddingXXL } from "src/styles/spacing";
import { Image, Pill, Text, useSafeArea } from "src/swsh-native";
import GatheringPublicReport from "../../../GatheringPublicReport";
import useGatheringInfoFullPublic from "../../hooks/useGatheringInfoFullPublic";
import { useGatheringPublicFullStore } from "../../hooks/useGatheringPublicFullStore";
import GatheringPublicFullHeaderAddon from "./components/GatheringPublicFullHeaderAddon";

const GatheringPublicFullHeader = ({
	thumbnail,
}: {
	thumbnail: GatheringHeaderInfoFragmentFragment["thumbnail"];
}) => {
	const gatheringId = useGatheringPublicFullStore((state) => state.gatheringId);
	const gatheringName = useGatheringPublicFullStore((state) => state.gatheringName);
	const gatheringRefCode = useGatheringPublicFullStore((state) => state.refCode);
	const joinLoading = useGatheringPublicFullStore((state) => state.joinLoading);
	const handleJoinGathering = useGatheringPublicFullStore((state) => state.handleJoinGathering);
	const setPopUpVariant = useGatheringPublicFullStore((state) => state.setPopUpVariant);
	const { gathering } = useGatheringInfoFullPublic(gatheringId);

	const renderWheelItem = useCallback<
		NonNullable<SharedGatheringHeaderProps<FullPublicPhotoFragmentFragment>["renderWheelItem"]>
	>(({ item, width, height, opacity, borderRadius }) => {
		const src = parseGatheringPreviewTemplateUrl({
			readUrl: item?.readUrl,
		});
		if (!src) return null;
		return (
			<Image
				alt="Photo"
				src={src}
				dim={{
					width,
					height,
				}}
				style={{
					borderRadius,
					overflow: "hidden",
					opacity,
				}}
			/>
		);
	}, []);
	const { device, native } = useDevice();
	const safeArea = useSafeArea();
	const router = useRouter();
	const { containerHorizontalPadding } = useGatheringHomePhotoSpacing();
	const headerPhotos = useGatheringPublicFullStore((state) => state.headerPhotos);
	const { presentModal } = useModal<GlobalModal>();

	const renderRowActions = useCallback(() => {
		const addonNodes: ReactNode[] = [];
		if (ADDONS) {
			const addons = [];
			for (const addon of gathering.addons ?? []) {
				if (addon !== null && (addon.label || addon.url)) {
					addons.push(addon);
				}
			}
			addons.map(async (addon) => {
				if (addon) {
					if (!addon.label && addon.url) {
						addon.label = parseUrlForLabel({
							url: addon.url,
							variant: addon.variant,
						});
					}
					addonNodes.push(
						<GatheringPublicFullHeaderAddon
							key={addon.addonId}
							addonId={addon.addonId}
							label={addon.label}
							url={addon.url}
							iconUrl={addon.icon}
							variant={addon.variant}
							backgroundColor={addon.backgroundColor}
						/>,
					);
				}
			});
		}
		return [
			<PillButton
				key="share"
				Icon={IconShare}
				onPress={async () => {
					const shareUrl =
						gathering?.shareUrl ?? `${config.share.base}/album/${gatheringRefCode}`;
					track("gathering_publicFullPreview_sharePress", {
						gatheringId,
					});
					presentModal("Share", {
						props: {
							header: {
								title: "Share album",
							},
							data: [
								{
									type: "Url",
									url: shareUrl,
									successAmplitude: {
										eventName: "gathering_publicFullPreview_shareSuccess",
										eventProperties: {
											gatheringId,
										},
									},
								},
							],
							preview:
								gathering?.shareQrCodeUrl && GATHERING_QR_CODE
									? {
											variant: "Image",
											alt: "QR Code",
											src: gathering.shareQrCodeUrl,
											dim: ({ width, height }) => {
												const size = Math.min(width, height) - paddingXXL;
												return {
													width: size,
													height: size,
												};
											},
										}
									: undefined,
						},
					});
				}}
			/>,
			gathering?.numPhotos != null && (
				<OpacityView>
					<PillButton
						testID="GatheringPreview-NumPhotos"
						key="photos"
						onPress={() => {
							track("gathering_publicFullPreview_photosCountPress", {
								gatheringId,
							});
							setPopUpVariant("SeePhotos");
						}}
						Icon={IconPhoto}
					>
						{`${abbreviateBigNumber(gathering.numPhotos)}`}
					</PillButton>
				</OpacityView>
			),
			gathering?.numTotalViews != null && (
				<OpacityView>
					<Pill key="viewers" Icon={IconView}>{`${abbreviateBigNumber(
						gathering.numTotalViews,
					)}`}</Pill>
				</OpacityView>
			),
			...addonNodes,
		];
	}, [
		gathering.addons,
		gathering.numPhotos,
		gathering.numTotalViews,
		gathering.shareQrCodeUrl,
		gathering?.shareUrl,
		gatheringId,
		gatheringRefCode,
		presentModal,
		setPopUpVariant,
	]);

	return (
		<View
			style={{
				alignItems: "center",
				marginBottom: paddingLarge,
				width: "100%",
			}}
		>
			<GatheringHeader
				testID="GatheringPreviewHeader"
				wheelData={headerPhotos}
				renderWheelItem={renderWheelItem}
				name={gathering?.name ?? gatheringName}
				variant={device === "desktop" ? "Wide" : "Regular"}
				wideOptions={{ horizontalPadding: containerHorizontalPadding }}
				thumbnailUrl_1x1={parseGatheringThumbnailUrl({
					readUrl: gathering?.thumbnail?.readUrl ?? thumbnail?.readUrl,
				})}
				renderRowActions={renderRowActions}
				renderEndSection={() => (
					<RoundedButton
						loading={joinLoading}
						onPress={() => {
							track("gathering_publicFullPreview_seePicsOfYouJoinPress", {
								gatheringId,
							});
							handleJoinGathering();
						}}
						sizeVariant="Large"
						colorVariant="Primary"
					>
						Find pics of you
					</RoundedButton>
				)}
			/>
			<View
				style={{
					position: "absolute",
					paddingTop: HEADER_SPACING.paddingTop + safeArea.top,
					flexDirection: "row",
					justifyContent: "space-between",
					width: "100%",
					paddingHorizontal: paddingLarge + containerHorizontalPadding,
				}}
			>
				{native ? (
					<Touchable onPress={router.back}>
						<Text semibold>Exit</Text>
					</Touchable>
				) : (
					<View />
				)}
				<GatheringPublicReport gatheringId={gatheringId} />
			</View>
		</View>
	);
};
export default memo(GatheringPublicFullHeader);
